// src/components/premium/DonationModal.tsx
"use client";

import React, { useState, useEffect, useRef } from 'react';
import { PayPalScriptProvider, PayPalButtons, type PayPalButtonsComponentProps } from '@paypal/react-paypal-js';
import { Loader2, Check, XIcon as X } from 'lucide-react'; // Usar XIcon y renombrar si es necesario

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  paypalClientId: string;
  onPaymentSuccess: (details: { transactionId: string; amount: number; paypalOrderId: string }) => void;
  userIdForPremium?: string; // ID del usuario para marcar como premium
}

// Interfaces para los datos y acciones de PayPal
interface PayPalOrderData {
  orderID: string;
  payerID?: string; // Opcional, pero a veces presente
}

const DonationModal: React.FC<DonationModalProps> = ({
  isOpen,
  onClose,
  paypalClientId,
  onPaymentSuccess,
  userIdForPremium
}) => {
  const [amount, setAmount] = useState<number>(5.00); // Default a $5.00 USD
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        if (!isProcessing) onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevenir scroll del fondo
      setErrorMsg(null); // Resetear error al abrir
      setShowSuccessScreen(false); // Resetear pantalla de éxito
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isProcessing]);

  const adjustAmount = (increment: number) => {
    const newAmount = Math.max(5, amount + increment); // Asegura que el mínimo sea 5
    setAmount(Number(newAmount.toFixed(2)));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = parseFloat(e.target.value);
    if (isNaN(newAmount)) {
      setAmount(5); // Si no es un número, volver al mínimo
    } else {
      setAmount(Math.max(5, newAmount)); // Asegura que el mínimo sea 5
    }
  };
  
  const handleAmountBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentVal = parseFloat(e.target.value);
      if (isNaN(currentVal) || currentVal < 5) {
          setAmount(5); // Si es inválido o menor al mínimo en blur, resetear a 5
      } else {
          setAmount(Number(currentVal.toFixed(2))); // Formatear a dos decimales
      }
  };


  const createOrder: PayPalButtonsComponentProps['createOrder'] = async (data, actions) => {
    console.log('[DonationModal] Creating PayPal order with amount:', amount);
    setIsProcessing(true);
    setErrorMsg(null);
    try {
      const response = await fetch('/api/paypal/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amount.toFixed(2), currency: 'USD' }),
      });
      const orderData = await response.json();
      if (!response.ok || !orderData.orderID) {
        throw new Error(orderData.error || 'Error al crear la orden de PayPal');
      }
      console.log('[DonationModal] PayPal order created, ID:', orderData.orderID);
      return orderData.orderID;
    } catch (err: any) {
      console.error('[DonationModal] Error creating order:', err);
      setErrorMsg(err.message || 'No se pudo crear la orden de pago.');
      setIsProcessing(false);
      throw err; // Re-lanzar para que PayPal SDK lo maneje si es necesario
    }
  };

  const onApprove: PayPalButtonsComponentProps['onApprove'] = async (data, actions) => {
    console.log('[DonationModal] Payment approved by PayPal, OrderID:', data.orderID);
    setIsProcessing(true);
    setErrorMsg(null);
    try {
      const response = await fetch('/api/paypal/capture-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderID: data.orderID }),
      });
      const captureResult = await response.json();

      if (!response.ok || !captureResult.success) {
        throw new Error(captureResult.error || 'Error al capturar el pago');
      }
      console.log('[DonationModal] Payment captured successfully:', captureResult);
      
      onPaymentSuccess({
        transactionId: captureResult.transactionId,
        amount: parseFloat(captureResult.amount),
        paypalOrderId: data.orderID
      });

      setShowSuccessScreen(true);
      setTimeout(() => {
        onClose(); 
        setShowSuccessScreen(false); 
        setAmount(5.00); // Resetear monto al mínimo
      }, 3000);

    } catch (err: any) {
      console.error('[DonationModal] Error capturing payment or notifying server:', err);
      setErrorMsg(err.message || 'Error procesando el pago.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const onError: PayPalButtonsComponentProps['onError'] = (err: any) => {
    console.error('[DonationModal] PayPal SDK error:', err);
    setErrorMsg('Ocurrió un error con PayPal. Por favor, intenta de nuevo.');
    setIsProcessing(false);
  };

  const onCancel: PayPalButtonsComponentProps['onCancel'] = (data: Record<string, unknown>) => {
    console.log('[DonationModal] Payment cancelled by user:', data);
    setIsProcessing(false);
    setErrorMsg('Pago cancelado.');
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div 
        ref={modalRef}
        className="bg-card text-card-foreground rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto flex flex-col"
      >
        {showSuccessScreen ? (
          <div className="p-8 text-center flex-grow flex flex-col justify-center items-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">¡Apoyo Recibido!</h2>
            <p className="text-muted-foreground mb-4">
              ¡Muchas gracias por tu contribución de ${amount.toFixed(2)} USD! Las funciones premium han sido desbloqueadas.
            </p>
            <p className="text-sm text-muted-foreground/80">
              Esta ventana se cerrará automáticamente...
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center p-5 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">Apoya con un Café</h2>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                disabled={isProcessing}
                aria-label="Cerrar modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 flex-grow">
              <p className="text-muted-foreground text-sm text-center">
                Tu apoyo nos ayuda a mantener y mejorar SnapSite. ¡Cualquier cantidad es bienvenida y desbloquea funciones geniales!
              </p>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1 text-center">
                  Cantidad (USD)
                </label>
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <button
                    onClick={() => adjustAmount(-1)}
                    disabled={amount <= 5 || isProcessing} // Mínimo 5
                    className="p-2 rounded-full bg-muted hover:bg-accent disabled:opacity-50"
                    aria-label="Disminuir cantidad"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                  </button>
                  <div className="flex items-baseline">
                    <span className="text-xl font-semibold text-primary mr-0.5">$</span>
                    <input
                      type="number"
                      min="5" // Mínimo 5
                      step="0.01"
                      value={amount.toFixed(2)}
                      onChange={handleAmountChange}
                      onBlur={handleAmountBlur}
                      disabled={isProcessing}
                      className="text-2xl font-bold text-foreground w-20 text-center bg-transparent border-b-2 border-input focus:border-primary outline-none disabled:opacity-50"
                    />
                  </div>
                  <button
                    onClick={() => adjustAmount(1)}
                    disabled={isProcessing}
                    className="p-2 rounded-full bg-muted hover:bg-accent disabled:opacity-50"
                    aria-label="Aumentar cantidad"
                  >
                     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  </button>
                </div>
                <div className="flex justify-center space-x-2">
                  {[5, 10, 15, 20].map((quickAmount) => ( // Botones actualizados
                    <button
                      key={quickAmount}
                      onClick={() => setAmount(quickAmount)}
                      disabled={isProcessing}
                      className="px-3 py-1 text-xs bg-secondary text-secondary-foreground rounded-full hover:bg-secondary/80 disabled:opacity-50"
                    >
                      ${quickAmount}
                    </button>
                  ))}
                </div>
              </div>
              
              {errorMsg && (
                <p className="text-sm text-destructive text-center bg-destructive/10 p-2 rounded-md">{errorMsg}</p>
              )}

              <div className="relative pt-2">
                {isProcessing && (
                  <div className="absolute inset-0 bg-card/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-md">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                    <span className="text-sm text-muted-foreground">Procesando pago...</span>
                  </div>
                )}
                
                {!paypalClientId ? (
                    <p className="text-center text-destructive text-sm p-3 border border-destructive/50 rounded-md">
                        La configuración de PayPal no está disponible. Por favor, contacta al administrador.
                    </p>
                ) : (
                    <PayPalScriptProvider 
                        options={{ 
                        "clientId": paypalClientId,
                        currency: "USD",
                        intent: "capture"
                        }}
                    >
                        <PayPalButtons
                        style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay" }}
                        disabled={isProcessing || amount < 5} // Mínimo 5
                        forceReRender={[amount, paypalClientId]}
                        createOrder={createOrder}
                        onApprove={onApprove}
                        onError={onError}
                        onCancel={onCancel}
                        />
                    </PayPalScriptProvider>
                )}
              </div>

              <p className="text-xs text-muted-foreground/70 text-center mt-4">
                Transacción segura procesada por PayPal.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DonationModal;
